import React, { FC, useCallback, useEffect } from "react";
import styled from "@emotion/styled";
import { Button } from "antd";
import ReactGA from "react-ga4";

import AnobisLogo from "src/assets/anobis-logo.svg";
import ImComLogo from "src/assets/imcom-logo.svg";
import CentralImage from "src/assets/anobis-group-central.svg";
import IMCOMLogin from "src/assets/IMCOMLogin.png";
import SwiggyLogo from "src/assets/swiggy-logo.svg";
import { useRedux } from "src/shared/hooks";
import { IRootState } from "src/shared/types";
import { loginAction, LoginActionPayload } from "src/store/login";
import colors from "src/styles/variables.scss";
import { RouteComponentProps } from "react-router";
import AuthService from "src/scenes/Login/auth.service";
import { getServiceName, ServiceHost, ServiceName } from "src/utils/getservicename";

const MainHolder = styled.main`
  background-color: ${colors.blackBackground};
  height: 100vh;
  height: calc(var(--vh, 1vh) * 100);
  position: absolute;
  width: inherit;
`;

const InnerHolder = styled.section`
  position: fixed;
  overflow-y: auto;
  display: block;
  height: 100%;
  width: inherit;
  color: ${colors.milkyWhite};
  display: grid;
  grid-template-rows: auto 25vh minmax(min-content, max-content) 60px;
`;

const AnobisLogoContainer = styled.section`
  display: flex;
  flex-direction: column;
  align-items: center;
  align-self: center;
  margin-top: 30px;
`;

const LogoText = styled.p`
  font-size: 20px;
  font-weight: normal;
  letter-spacing: 4px;
  color: ${colors.paleBlue};
  margin-top: 8.5px;
  margin-bottom: 0;
  text-transform: uppercase;
`;

const LogoSubText = styled.p`
  opacity: 0.75;
  font-size: 12px;
  font-weight: normal;
  letter-spacing: 2px;
  color: ${colors.downRed};
  margin-top: 1px;
`;

const CentralGraphic = styled.section`
  align-items: center;
  text-align: center;
  position: relative;
  height: 100%;

  > img {
    position: absolute;
    z-index: 1;
    top: 0;
    left: 50%;
    height: inherit;
    transform: translateX(-50%) translateY(20px);
  }
`;

const LoginSection = styled.section`
  padding: 53px;
  align-self: center;
  background: ${colors.bluishGray};
  height: 100%;
`;

const Footer = styled.footer`
  display: flex;
  justify-content: center;
  align-items: flex-start;
  background: ${colors.bluishGray};

  > span {
    opacity: 0.59;
    font-size: 10px;
    letter-spacing: 1px;
    color: ${colors.milkyWhite};
    margin-left: 4.5px;
    line-height: 1;
  }
`;

const IMCOMLoginMainContainer = styled.main`
  background-image: url(${IMCOMLogin});
  background-size: cover;
  background-position: center;
  height: 100vh;
  position: absolute;
  width: 100%;
`;

const IMCOMLoginInnerContainer = styled.section`
  position: absolute;
  bottom: 53px;
  left: 50%;
  transform: translateX(-50%);
  width: 90%;
`;

const IMCOMLoginButton = styled(Button)`
  background-color: white;
  color: black;
  border-radius: 8px;
  border: none;
  width: 100%;
  height: 50px;

  &:hover {
    background-color: #f0f0f0;
    color: black;
  }
`;

const LoginComponent: FC<RouteComponentProps> = ({ history }) => {
  const { loginAction: dispatchLoginAction, pending: isPending } = useRedux<
    IRootState,
    { pending: boolean },
    {
      loginAction(payload: LoginActionPayload);
    }
  >((state) => state.login, {
    loginAction,
  });

  useEffect(() => {
    ReactGA.send({ hitType: "pageview", page: window.location.pathname, title: "Login" });
  }, []);

  const handleLogin = useCallback(async (e) => {
    e.preventDefault();
    const { response: token, error } = await AuthService.authenticate();

    if (!token) {
      console.error(error);
      return;
    }

    dispatchLoginAction({
      token,
      callbackFn: () => handleRedirectionAfterLogin(),
    });
  }, []);

  const handleRedirectionAfterLogin = () => {
    const currentUrl = window.location.href;
    if (currentUrl.includes(ServiceHost.ANOBIS)) {
      history.push("/");
    } else if (currentUrl.includes(ServiceHost.ANOBIS_IMCOM)) {
      history.push("/im-com-app");
    } else if (currentUrl.includes(ServiceHost.ANOBIS_INDIA_2)) {
      history.push("/india-2-app");
    } else {
      history.push("/");
    }
  };

  const logoSrc = getServiceName() === ServiceName.ANOBIS_IMCOM ? ImComLogo : AnobisLogo;
  const serviceName = getServiceName();

  if (serviceName === ServiceName.ANOBIS_IMCOM) {
    return (
      <IMCOMLoginMainContainer>
        <IMCOMLoginInnerContainer>
          <IMCOMLoginButton
            type="primary"
            htmlType="submit"
            size="large"
            block
            loading={isPending}
            onClick={handleLogin}
          >
            Login Securely
          </IMCOMLoginButton>
        </IMCOMLoginInnerContainer>
      </IMCOMLoginMainContainer>
    );
  }

  return (
    <MainHolder>
      <InnerHolder>
        <AnobisLogoContainer>
          <img src={logoSrc} alt={`${serviceName} Logo`} />
          <LogoText>{serviceName}</LogoText>
          <LogoSubText>for swiggy</LogoSubText>
        </AnobisLogoContainer>
        <CentralGraphic>
          <img src={CentralImage} alt="Anobis central graphic" />
        </CentralGraphic>
        <LoginSection>
          <Button type="primary" htmlType="submit" size="large" block loading={isPending} onClick={handleLogin}>
            Login Securely
          </Button>
        </LoginSection>
        <Footer>
          <img src={SwiggyLogo} alt="Swiggy Logo" />
          <span>A Swiggy product</span>
        </Footer>
      </InnerHolder>
    </MainHolder>
  );
};

export default LoginComponent;
